// DEPLOY: /var/www/woody.wtf/frontend/src/components/Navbar.js
// Shared site navbar used by every page. Logo (NinjaMark + chart.ninja wordmark) links to https://chart.ninja. Alert bell + inbox (AlertBell) for signed-in users.
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AlertBell from './AlertBell';

/**
 * Shared site navbar.
 *
 * Props:
 *   backTo       {{ label, href }}  optional back link rendered next to the logo (e.g. "← Admin")
 *   children     optional center content (e.g. StrategyBuilder's picker button)
 *   compact      reduced horizontal padding for full-height app pages
 *   hideNavLinks hide the right-hand side entirely
 *   rightLinks   [{ label, href, muted }] extra links before the Dashboard link
 *   hideUser     hide the Dashboard link and username, keep Sign out (mobile app pages)
 */
// chart.ninja mark: a ninja hood whose eye-slit is a rising chart line (same as public/chart-ninja.svg).
export function NinjaMark({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <rect x="6" y="6" width="108" height="108" rx="30" fill="#1C2330" />
      <rect x="6" y="44" width="108" height="34" fill="#05070A" />
      <polyline points="18,70 34,62 48,66 64,54 78,58 100,46" fill="none" stroke="#F5B83D" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="100" cy="46" r="5" fill="#F5B83D" />
    </svg>
  );
}

export default function Navbar({ backTo, children, compact = false, hideNavLinks = false, rightLinks = [], hideUser = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const onDashboard = location.pathname === '/dashboard';

  const s = {
    nav: {
      background: 'var(--surface)',
      borderBottom: '1px solid var(--border)',
      padding: compact ? '0 14px' : '0 32px',
      height: '56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
    },
    left: { display: 'flex', alignItems: 'center', gap: '16px' },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Space Grotesk', sans-serif",
      fontSize: '17px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      color: 'var(--text)',
      textDecoration: 'none',
    },
    navRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
      color: 'var(--text-muted)',
    },
    navLink: { color: 'var(--accent)', fontSize: '13px', textDecoration: 'none' },
    navLinkMuted: { color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none' },
    logoutBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--accent)',
      fontSize: '13px',
      fontFamily: "'Space Grotesk', sans-serif",
      padding: '6px 12px',
      borderRadius: 'var(--radius)',
    },
  };

  return (
    <nav style={s.nav}>
      <div style={s.left}>
        <a
          href="https://chart.ninja"
          style={s.logo}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          <NinjaMark size={26} />
          <span>chart<span style={{ color: 'var(--accent)' }}>.</span>ninja</span>
        </a>
        {backTo && <Link to={backTo.href} style={s.navLink}>{backTo.label}</Link>}
      </div>
      {children}
      {!hideNavLinks && (
        <div style={s.navRight}>
          {rightLinks.map(l => (
            <a key={l.href} href={l.href} style={l.muted ? s.navLinkMuted : s.navLink}>{l.label}</a>
          ))}
          {!hideUser && <Link to="/help" style={s.navLinkMuted}>Help</Link>}
          {!onDashboard && !hideUser && <Link to="/dashboard" style={s.navLink}>Dashboard</Link>}
          {user && <AlertBell />}
          {!hideUser && <span>{user?.username}</span>}
          <button style={s.logoutBtn} onClick={handleLogout}>Sign out</button>
        </div>
      )}
    </nav>
  );
}
